/*
 * yojo-pdp-gap.js — the PDP "how far to home delivery" line (2026-10-01).
 *
 * Markup and first paint: snippets/yojo-pdp-gap.liquid (server-rendered from
 * yojo-basket-ladder mode 'compute', so the minimum has one owner). This file
 * keeps the line true after the page loads:
 *   - the size pill changes (input.yojo-pdp__pack-input 'change') or the
 *     quantity changes: recompute against the selected size x quantity;
 *   - the cart changes (any add path, the drawer, the delivery/pickup switch):
 *     re-read /cart.js and recompute. The theme announces cart changes on
 *     'cart:updated', 'yojo:cart:refreshed' and 'yojo:subtotal:updated'.
 *
 * States: 'gap' (neutral line + jump link to the companion strip), 'met' (this
 * add reaches the minimum; matcha), 'silent' (pickup intent, cart already at
 * the minimum, or the chosen size cannot be bought). Pickup has no minimum, so
 * the line is silent there, exactly like the cart ladder.
 *
 * While there is a gap, the companion strip on the page shows the same
 * sentence under its heading and switches each card's one-tap "+" to the
 * buyable size that best closes the gap (the smallest size that covers it,
 * otherwise the largest). Cards that close the gap are listed first. When the
 * gap is gone every card returns to its default size and order.
 *
 * No amounts live here: the minimum arrives in data-min-cents, the copy in
 * data-tpl-gap / data-tpl-met (locales/he.json yojo.basket.pdp_gap*).
 *
 * 2026-10-14 gift ladder (P1): when the snippet carries data-ladder (the
 * switch is on), the line also says what this size and quantity unlocks on
 * the gift ladder: an add that crosses a step says so, between steps it
 * counts down to the next step. Under the delivery minimum it stays exactly
 * the line above. The basis is the ladder's (paid products after discounts,
 * gift lines excluded), the same rule as yojo-basket-ladder compute.
 */
(function () {
  'use strict';

  var CART_EVENTS = ['cart:updated', 'yojo:cart:refreshed', 'yojo:subtotal:updated'];

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  // Same shape as money_without_trailing_zeros on this store: "83 ₪", "82.50 ₪".
  function moneyShort(cents) {
    var n = Math.round(cents) / 100;
    return (n % 1 === 0 ? String(n) : n.toFixed(2)) + ' ₪';
  }

  // Same shape as yojo-pdp-v2.js moneyFormat (the strip's server-rendered `| money`).
  function moneyFull(cents) {
    return (Math.round(cents) / 100).toFixed(2) + ' ₪';
  }

  function setup(el) {
    if (el.__yojoGap) return;
    el.__yojoGap = true;

    var root = el.closest('product-page') || document;
    var min = toInt(el.getAttribute('data-min-cents'));
    if (min <= 0) return;

    var tplGapPlain = el.getAttribute('data-tpl-gap') || '';
    var tplGapDisc = el.getAttribute('data-tpl-gap-discount') || tplGapPlain;
    function tplGap() { return state.discount ? tplGapDisc : tplGapPlain; }
    var tplMet = el.getAttribute('data-tpl-met') || '';
    var textEl = el.querySelector('[data-yojo-gap-text]');
    var linkEl = el.querySelector('[data-yojo-gap-link]');
    var strip = null;
    if (linkEl) {
      var hash = (linkEl.getAttribute('href') || '').replace(/^#/, '');
      strip = hash ? document.getElementById(hash) : null;
    }
    var connector = strip ? strip.querySelector('[data-yojo-gap-connector]') : null;
    var stripList = strip ? strip.querySelector('.yojo-companions__list') : null;
    // Each card (li) carries its sizes on the price line ([data-yojo-gap-sizes]).
    var stripItems = strip ? Array.prototype.slice.call(strip.querySelectorAll('.yojo-companions__item'))
      .filter(function (li) { return !!li.querySelector('[data-yojo-gap-sizes]'); }) : [];

    var productData = null;
    try {
      var pj = root.querySelector('[data-js-product-json]');
      productData = pj ? JSON.parse(pj.textContent.trim()) : null;
    } catch (e) { productData = null; }

    var ladder = null;
    try { ladder = el.hasAttribute('data-ladder') ? JSON.parse(el.getAttribute('data-ladder')) : null; } catch (e) { ladder = null; }
    var tplCrossFirst = el.getAttribute('data-tpl-cross-first') || '';
    var tplCrossNext = el.getAttribute('data-tpl-cross-next') || '';
    var tplCrossDelivery = el.getAttribute('data-tpl-cross-delivery') || '';
    var tplGapGift = el.getAttribute('data-tpl-gap-gift') || '';
    var tplGapDelivery = el.getAttribute('data-tpl-gap-delivery') || '';

    var state = {
      basis: toInt(el.getAttribute('data-basis-cents')),
      cart: toInt(el.getAttribute('data-cart-cents')),
      intent: (el.getAttribute('data-intent') === 'pickup' && !window.YOJO_PICKUP_PAUSED) ? 'pickup' : 'delivery',
      inCart: el.getAttribute('data-in-cart') ? el.getAttribute('data-in-cart').split(',') : [],
      discount: el.getAttribute('data-discount') === 'true'
    };

    function selected() {
      var r = root.querySelector('input.yojo-pdp__pack-input:checked');
      if (r) {
        return { id: String(r.value), price: toInt(r.getAttribute('data-pack-price')), available: !r.disabled };
      }
      var hidden = root.querySelector('[data-js-product-form] input[name="id"]');
      if (!hidden || !productData || !productData.variants) return null;
      var v = productData.variants.filter(function (x) { return String(x.id) === String(hidden.value); })[0];
      return v ? { id: String(v.id), price: toInt(v.price), available: v.available !== false } : null;
    }

    function quantity() {
      var q = root.querySelector('[data-js-qty-input]');
      return Math.max(1, toInt(q && q.value) || 1);
    }

    // Whole shekels, rounded up (copy deck rule for every gap).
    function ceilShekel(cents) { return Math.ceil(cents / 100) * 100; }

    function computeLadder() {
      var sel = selected();
      if (!sel || !sel.available) return { mode: 'silent', gap: 0 };
      var add = state.inCart.indexOf(sel.id) === -1 ? sel.price * quantity() : 0;
      var total = state.cart + add;
      var after = state.basis + add;
      if (state.intent !== 'pickup' && total < min) {
        return { mode: 'gap', gap: min - total, text: tplGap().replace('__AMOUNT__', moneyShort(ceilShekel(min - total))) };
      }
      var steps = ladder.filter(function (s) { return !(state.intent === 'pickup' && s.kind === 'delivery'); });
      var crossed = steps.filter(function (s) { return state.basis < s.amount && s.amount <= after; });
      if (crossed.length) {
        var top = crossed[crossed.length - 1];
        var earlier = steps.some(function (s) { return s.kind === 'gift' && s.amount < top.amount && s.amount <= after; });
        var text = top.kind === 'delivery' ? tplCrossDelivery
          : (earlier ? tplCrossNext : tplCrossFirst).replace('__GIFT__', top.gift);
        return { mode: 'met', gap: 0, text: text };
      }
      if (state.intent !== 'pickup' && state.cart < min && total >= min) return { mode: 'met', gap: 0, text: tplMet };
      var next = steps.filter(function (s) { return s.amount > after; })[0];
      if (!next) return { mode: 'silent', gap: 0 };
      var gap = next.amount - after;
      var amount = moneyShort(ceilShekel(gap));
      return {
        mode: 'gap',
        gap: gap,
        text: next.kind === 'delivery' ? tplGapDelivery.replace('__AMOUNT__', amount)
          : tplGapGift.replace('__AMOUNT__', amount).replace('__GIFT__', next.gift)
      };
    }

    function compute() {
      if (ladder) return computeLadder();
      if (state.intent === 'pickup' || state.cart >= min) return { mode: 'silent', gap: 0 };
      var sel = selected();
      if (!sel || !sel.available) return { mode: 'silent', gap: 0 };
      // Already in the cart: the cart total counts it, so do not count it twice.
      var add = state.inCart.indexOf(sel.id) === -1 ? sel.price * quantity() : 0;
      var gap = min - state.cart - add;
      return { mode: gap > 0 ? 'gap' : 'met', gap: Math.max(0, gap) };
    }

    function parseSizes(item) {
      var holder = item.querySelector('[data-yojo-gap-sizes]');
      try { return JSON.parse((holder && holder.getAttribute('data-yojo-gap-sizes')) || '[]'); }
      catch (e) { return []; }
    }

    // The buyable size that best closes the gap: the cheapest that covers it,
    // otherwise the largest (it gets closest).
    function bestSize(sizes, gap) {
      var covers = sizes.filter(function (s) { return s.price >= gap; })
        .sort(function (a, b) { return a.price - b.price; });
      if (covers.length) return covers[0];
      return sizes.slice().sort(function (a, b) { return b.price - a.price; })[0] || null;
    }

    function setCardSize(item, size) {
      if (!size) return;
      var idInput = item.querySelector('[data-yojo-gap-id]');
      if (!idInput || idInput.value === String(size.id)) return;
      idInput.value = String(size.id);
      // Packing field of the chosen size only (the card quick-add pattern).
      item.querySelectorAll('[data-line-property-for]').forEach(function (f) {
        f.disabled = f.getAttribute('data-line-property-for') !== String(size.id);
      });
      var price = item.querySelector('[data-yojo-gap-price]');
      if (price) {
        price.textContent = moneyFull(size.price);
        if (size.title && size.title !== 'Default Title') {
          var tag = document.createElement('i');
          tag.textContent = '· ' + size.title;
          price.appendChild(document.createTextNode(' '));
          price.appendChild(tag);
        }
      }
    }

    function syncStrip(result) {
      if (!strip || !stripItems.length) return;
      var gapMode = result.mode === 'gap';
      if (connector) {
        connector.textContent = gapMode ? (result.text || tplGap().replace('__AMOUNT__', moneyShort(result.gap))) : '';
        connector.hidden = !gapMode;
      }
      var ranked = stripItems.map(function (item, i) {
        var sizes = parseSizes(item);
        var holder = item.querySelector('[data-default-variant]');
        var defId = holder ? holder.getAttribute('data-default-variant') : '';
        var def = sizes.filter(function (s) { return String(s.id) === defId; })[0] || sizes[0];
        var pick = gapMode ? bestSize(sizes, result.gap) : def;
        setCardSize(item, pick);
        return { item: item, i: i, closes: !!(gapMode && pick && pick.price >= result.gap), price: pick ? pick.price : 0 };
      });
      if (!stripList) return;
      if (gapMode) {
        ranked.sort(function (a, b) {
          if (a.closes !== b.closes) return a.closes ? -1 : 1;
          return a.closes ? a.price - b.price : b.price - a.price;
        });
      } else {
        ranked.sort(function (a, b) { return a.i - b.i; });
      }
      ranked.forEach(function (r) { stripList.appendChild(r.item); });
    }

    // a11-F5 (swarm 2026-10-01): the line under the add button changes when the
    // size, the quantity or the cart changes. Say the new line politely, once
    // the shopper settles (typing a quantity says only the last value, and the
    // size announcement in #screen-reader-info goes first). The first render
    // only records what is already on screen. A line that disappears (pickup,
    // minimum reached, sold-out size) is not announced.
    var liveEl = el.querySelector('[data-yojo-gap-live]');
    var spoken = null;
    var speakTimer = 0;
    function announce(message) {
      if (!liveEl) return;
      if (spoken === null) { spoken = message; return; }
      if (message === spoken) return;
      spoken = message;
      clearTimeout(speakTimer);
      liveEl.textContent = '';
      if (message) speakTimer = setTimeout(function () { liveEl.textContent = message; }, 400);
    }

    function render() {
      var result = compute();
      var message = '';
      el.setAttribute('data-state', result.mode);
      el.hidden = result.mode === 'silent';
      if (result.text) message = result.text;
      else if (result.mode === 'gap') message = tplGap().replace('__AMOUNT__', moneyShort(result.gap));
      else if (result.mode === 'met') message = tplMet;
      if (textEl && message) textEl.textContent = message;
      if (linkEl) linkEl.hidden = !(result.mode === 'gap' && stripItems.length);
      syncStrip(result);
      announce(message);
    }

    var timer = null;
    function refreshCart() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (cart) {
            if (!cart) return;
            state.cart = toInt(cart.total_price);
            state.basis = Math.max(0, state.cart - (cart.items || []).reduce(function (s, it) {
              var src = String((it.properties && it.properties._yojo_src) || '');
              return src.indexOf('gift') !== -1 ? s + toInt(it.final_line_price) : s;
            }, 0));
            /* 2026-11-04 pickup pause: a stale pickup attribute reads as delivery. */
            state.intent = (cart.attributes && cart.attributes.fulfillment_intent === 'pickup' && !window.YOJO_PICKUP_PAUSED) ? 'pickup' : 'delivery';
            state.inCart = (cart.items || []).map(function (it) { return String(it.variant_id); });
            state.discount = toInt(cart.total_discount) > 0;
            render();
          })
          .catch(function () { /* keep the last true state; the next cart event retries */ });
      }, 120);
    }

    root.querySelectorAll('input.yojo-pdp__pack-input').forEach(function (r) {
      r.addEventListener('change', function () { if (r.checked) render(); });
    });
    var qty = root.querySelector('[data-js-qty-input]');
    if (qty) {
      qty.addEventListener('change', render);
      qty.addEventListener('input', render);
    }
    CART_EVENTS.forEach(function (name) { document.addEventListener(name, refreshCart); });
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });

    if (linkEl && strip) {
      linkEl.addEventListener('click', function (e) {
        e.preventDefault();
        var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        strip.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
        try { strip.focus({ preventScroll: true }); } catch (err) { strip.focus(); }
      });
    }

    render();
  }

  function initAll() {
    document.querySelectorAll('[data-yojo-gap]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
